<template>
  <a-modal :title="modalTitle" :visible="visible" @ok="handleOk" @cancel="$emit('update:visible', false)" @afterClose="closeModal" okText="确认" cancelText="取消" width="500px">
    <a-form ref="formRef" :model="formState" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">

      <a-form-item label="品类名称" name="value" :rules="[{ required: true, message: '请输入促销品类名称' }]">
        <a-input v-model:value="formState.value" placeholder="请输入促销品类名称" />
      </a-form-item>

      <a-form-item label="品类图片" name="imageUuid">
        <a-upload v-model:file-list="fileList" name="file" list-type="picture-card" :show-upload-list="false" :custom-request="uploadImage" :before-upload="beforeUpload" class="upload-wrapper">

          <div v-if="formState.imageUuid" class="upload-preview">
            <img :src="imageUrl + formState.imageUuid" alt="品类图片" />
            <div class="upload-actions" @click.stop="removeImage">
              <DeleteOutlined />
            </div>
          </div>

          <div v-else>
            <loading-outlined v-if="loading"></loading-outlined>
            <plus-outlined v-else></plus-outlined>
            <div class="ant-upload-text">上传图片</div>
          </div>
        </a-upload>
      </a-form-item>

      <a-form-item v-if="isEdit" label="ID" name="id" style="display: none;">
        <a-input v-model:value="formState.id" disabled />
      </a-form-item>

    </a-form>
  </a-modal>
</template>

<script>
import { PlusOutlined, LoadingOutlined, DeleteOutlined } from "@ant-design/icons-vue";
// 接口复用：因为接口地址相同，直接复用原有的 api 方法即可
import { AddCategory, UpdateCategory } from "/src/api/shopping.js";
import { Upload } from "/src/api/upload.js";
import { message } from "ant-design-vue";
import { cloneDeep } from "lodash";

export default {
  name: "促销品类新增编辑弹窗", // 组件名称修改
  components: {
    PlusOutlined,
    LoadingOutlined,
    DeleteOutlined,
  },
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) }
  },
  emits: ['update:visible', 'ok'],
  data() {
    return {
      loading: false,
      imageUrl: '/api/file/img/',
      fileList: [],
      formState: { // 表单数据模型
        id: undefined,
        value: undefined,
        imageUuid: undefined,
        // =================关键修改点=================
        name: '促销品类', // 根据截图，此处改为 "促销品类"
        category: '商城设置', // 此处保持 "商城设置"
        // ===========================================
      },
      formRef: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.formState.id;
    },
    modalTitle() {
      // 标题文案修改
      return this.isEdit ? "编辑促销品类" : "新增促销品类";
    }
  },
  watch: {
    record: {
      handler(newRecord) {
        if (newRecord && newRecord.id) {
          this.formState = { ...this.formState, ...cloneDeep(newRecord) };
        }
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    /**
     * 核心重置方法
     */
    resetFormState() {
      // 1. 重置表单数据模型
      this.formState = {
        id: undefined,
        value: undefined,
        imageUuid: undefined,
        // =================关键修改点=================
        name: '促销品类', // 重置时也要确保默认值是 "促销品类"
        category: '商城设置',
        // ===========================================
      };
      // 2. 重置其他状态
      this.fileList = [];
      this.loading = false;
    },

    removeImage() {
      this.formState.imageUuid = undefined;
      this.fileList = [];
      message.success('图片已移除');
    },

    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 文件!');
      }
      return isJpgOrPng;
    },

    async uploadImage({ file, onSuccess, onError }) {
      this.loading = true;
      let formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'portrait');
      formData.append('system', 'true');

      try {
        const res = await Upload(formData);

        if (res.errCode === 0 && res.data) {
          this.formState.imageUuid = res.data;
          message.success('图片上传成功');
          onSuccess(res);
        } else {
          message.error('上传失败：' + (res.message || '未知错误'));
          onError(new Error('Upload failed'));
        }
      } catch (error) {
        console.error("uploadImage error:", error);
        message.error('网络错误，上传失败');
        onError(error);
      } finally {
        this.loading = false;
      }
    },

    // 模态框确认按钮的回调
    async handleOk() {
      try {
        await this.$refs.formRef.validate();

        let res;
        if (this.isEdit) {
          // 编辑接口 PUT
          res = await UpdateCategory(this.formState);
        } else {
          const { id, ...addData } = this.formState;
          // 新增接口 POST
          // 此时 addData.name 已经是 "促销品类"
          res = await AddCategory(addData);
        }

        if (res.errCode === 0) {
          message.success("操作成功");
          this.resetFormState();
          this.$emit('ok');
          this.$emit('update:visible', false);
        } else {
          message.error(res.errMsg || "操作失败");
        }
      } catch (error) {
        if (error && error.errorFields && error.errorFields.length > 0) {
          const firstErrorMsg = error.errorFields[0].errors[0];
          message.warning(`请检查表单：${firstErrorMsg}`);
        } else if (error !== false) {
          console.error('API Error:', error);
          message.error('请求异常，请稍后重试');
        }
      }
    },

    closeModal() {
      this.resetFormState();
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields();
      }
    }
  },
};
</script>

<style scoped>
/* 样式保持一致 */
.upload-wrapper {
  width: 104px;
  height: 104px;
}

.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 5;
}

.upload-preview:hover .upload-actions {
  opacity: 1;
}
</style>